import React, { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import LocalizedLink, { useLocalizedPath } from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import { findPostBySlug } from "@/data/posts";
import { findProjectBySlug } from "@/data/projects";
import { trackEvent } from "@/utils/analytics";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import NewsletterSignup from "@/components/NewsletterSignup";
import "./InsightPage.scss";

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

interface PostContent {
  lede: string;
  sections: { heading: string; body: string[] }[];
}

const InsightPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const localizedPath = useLocalizedPath();

  const post = slug ? findPostBySlug(slug) : undefined;
  const relatedProject = post?.relatedProjectSlug
    ? findProjectBySlug(post.relatedProjectSlug)
    : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  useEffect(() => {
    if (post) {
      trackEvent("insight_open", { slug: post.slug });
    }
  }, [post]);

  const content = post
    ? (t(`insights.posts.${post.slug}`, { returnObjects: true }) as PostContent | null)
    : null;

  useDocumentMeta(
    post && content
      ? {
          title: `${post.title} | Devbloom`,
          description: content.lede,
          canonical: `https://devbloom.dev/insights/${post.slug}`,
          ogTitle: `${post.title} | Devbloom`,
          ogDescription: content.lede,
          ogType: "article",
        }
      : null
  );

  if (!post || !content) {
    return <Navigate to="/" replace />;
  }

  const formattedDate = new Date(post.date).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <article className="insight">
      <div className="container">
        <LocalizedLink to="/insights" className="insight-back">
          {t("insights.backToInsights")}
        </LocalizedLink>

        <header className="insight-hero">
          <div className="insight-kicker">
            <time dateTime={post.date}>{formattedDate}</time>
            <span className="insight-kicker-dot" aria-hidden="true">·</span>
            <span>{t("insights.readingTime", { min: post.readingMinutes })}</span>
          </div>

          <h1 className="insight-title">{post.title}</h1>

          <p className="insight-lede">{content.lede}</p>

          <div className="insight-tags">
            {post.tags.map((tag) => (
              <span className="insight-tag" key={tag}>{tag}</span>
            ))}
          </div>
        </header>

        <div className="insight-body">
          {content.sections.map((section, i) => (
            <section key={i} className="insight-section">
              <h2 className="insight-section-heading">{section.heading}</h2>
              {section.body.map((paragraph, j) => (
                <p key={j} className="insight-para">{paragraph}</p>
              ))}
            </section>
          ))}
        </div>

        {relatedProject && (
          <aside className="insight-related">
            <p className="insight-related-label">{t("insights.relatedWorkLabel")}</p>
            <LocalizedLink
              to={`/work/${relatedProject.slug}`}
              className="insight-related-link"
              onClick={() => trackEvent("insight_related_click", { slug: post.slug, project: relatedProject.slug })}
            >
              {relatedProject.name} → {t("insights.relatedWork")}
            </LocalizedLink>
          </aside>
        )}

        <section className="insight-cta">
          <p className="insight-cta-sub">{t("insights.contactCta")}</p>
          <a
            href={`${localizedPath("/")}#contact-section`}
            className="insight-cta-btn"
            onClick={() => trackEvent("insight_cta_click", { slug: post.slug })}
          >
            {t("insights.contactCtaBtn")} <ArrowIcon />
          </a>
        </section>

        <NewsletterSignup variant="card" />
      </div>
    </article>
  );
};

export default InsightPage;
